import { useMemo } from 'react';
import { useCustomLocation } from './use-custom-location';

export type InitialQuery = {
    agent?: string;
    externalToken?: string;
    origin?: string;
    discounts?: string[];
};

const INITIAL_QUERY_PARAMETER_NAMES = ['agent', 'externalToken', 'origin'] as const;
const DISCOUNTS = 'discounts';

const storedDiscounts = (): string[] | undefined => {
    const value = localStorage.getItem(DISCOUNTS);
    if (!value) return undefined;

    try {
        const discounts = JSON.parse(value);
        return Array.isArray(discounts) && discounts.every(discount => typeof discount === 'string') ? discounts : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Gets initial query parameters from the URL or from local storage.
 */
export function useInitialQuery(): InitialQuery {
    const { search, isReady } = useCustomLocation();

    return useMemo<InitialQuery>(() => {
        if (!isReady) return {};

        const searchParameters = new URLSearchParams(search);
        const initialQuery: InitialQuery = {};

        INITIAL_QUERY_PARAMETER_NAMES.forEach(parameter => {
            const value = searchParameters.get(parameter);
            if (value) {
                localStorage.setItem(parameter, value);
                initialQuery[parameter] = value;
            } else {
                const storedValue = localStorage.getItem(parameter);
                if (storedValue) initialQuery[parameter] = storedValue;
            }
        });

        const discounts = searchParameters.getAll(DISCOUNTS).filter(Boolean);
        if (discounts.length > 0) {
            localStorage.setItem(DISCOUNTS, JSON.stringify(discounts));
            initialQuery.discounts = discounts;
        } else {
            initialQuery.discounts = storedDiscounts();
        }

        return initialQuery;
    }, [isReady, search]);
}
